import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Minus, Sparkle, Spinner, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { rememberBack } from "@/utils/propertyBackTracker";

export default function Analytics() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(null); // pid being compared
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/analytics/overview");
        setOverview(r.data);
      } catch (e) {
        toast.error("Errore nel caricamento delle statistiche");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onCompare = async (pid) => {
    setComparing(pid);
    setInsight(null);
    try {
      const r = await api.post(`/analytics/compare/${pid}`);
      setInsight(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confronto non disponibile");
    } finally {
      setComparing(null);
    }
  };

  const props = overview?.properties || [];
  const hasData = props.some((p) => p.views > 0);

  return (
    <AppShell title="Performance" breadcrumb="Performance">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm text-[#6B6A65] hover:text-[#7C8B6E] mb-6"
          data-testid="analytics-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Torna alla dashboard
        </button>

        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <span className="overline">Performance</span>
            <h1 className="font-editorial text-4xl lg:text-5xl mt-3 leading-tight tracking-tight" data-testid="analytics-title">
              Capisci cosa funziona<br/>
              <span className="italic-accent text-[#7C8B6E]">davvero.</span>
            </h1>
            <p className="text-[#6B6A65] mt-3 max-w-xl">
              Aggrega views e contatti da tutti i portali. Confronta gli annunci tra loro
              per capire quali convertono meglio — e perché.
            </p>
          </div>
          {overview && (
            <div className="flex gap-6 lg:gap-10 text-right" data-testid="analytics-totals">
              <Stat label="Views totali" value={overview.total_views.toLocaleString("it-IT")} />
              <Stat label="Richieste totali" value={overview.total_leads.toLocaleString("it-IT")} />
              <Stat label="Conv media" value={`${overview.avg_conv_rate}%`} />
            </div>
          )}
        </div>

        {loading && <div className="text-[#6B6A65] py-20 flex justify-center"><Spinner size={28} className="animate-spin" /></div>}

        {!loading && props.length === 0 && (
          <div className="card-flat p-12 text-center" data-testid="analytics-empty">
            <ChartBar size={40} className="mx-auto text-[#6B6A65]" weight="light" />
            <p className="mt-4 text-[#6B6A65]">Non hai ancora annunci. Crea il primo annuncio per iniziare a tracciare la performance.</p>
            <button onClick={() => navigate("/dashboard/property/new")} className="btn-primary mt-6" data-testid="analytics-cta-create">
              Crea immobile
            </button>
          </div>
        )}

        {!loading && props.length > 0 && !hasData && (
          <div className="border border-[#E5E7E0] bg-[#F1F4ED] p-6 mb-8 text-sm text-[#7C8B6E]" data-testid="analytics-no-data-banner">
            <strong>Nessun dato di performance ancora.</strong> Apri un annuncio, vai nella tab "Performance" e
            inserisci settimanalmente i numeri dei portali (li trovi nei loro dashboard pro). Il link
            pubblico Pronto traccia automaticamente views e richieste.
          </div>
        )}

        {!loading && props.length > 0 && (
          <div className="card-flat overflow-hidden" data-testid="analytics-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F4ED] text-[#6B6A65]">
                <tr>
                  <th className="text-left p-4 font-medium">Annuncio</th>
                  <th className="text-right p-4 font-medium">Online</th>
                  <th className="text-right p-4 font-medium">Views</th>
                  <th className="text-right p-4 font-medium">Richieste</th>
                  <th className="text-right p-4 font-medium">Conv</th>
                  <th className="text-center p-4 font-medium hidden md:table-cell">vs media</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {props.map((p) => (
                  <tr key={p.id} className="border-t border-[#E5E7E0] hover:bg-[#FFFFFF]" data-testid={`analytics-row-${p.id}`}>
                    <td className="p-4">
                      <button
                        onClick={() => { rememberBack(p.id, "Statistiche", "/dashboard/analytics"); navigate(`/dashboard/property/${p.id}`); }}
                        className="text-left hover:text-[#7C8B6E] transition-colors"
                      >
                        <div className="font-medium leading-tight">{p.title}</div>
                        <div className="text-xs text-[#6B6A65] mt-1">{p.city || "—"}{p.price ? ` · € ${Number(p.price).toLocaleString("it-IT")}` : ""}</div>
                      </button>
                    </td>
                    <td className="p-4 text-right text-[#6B6A65]">{p.days_online}g</td>
                    <td className="p-4 text-right tabular-nums">{p.views.toLocaleString("it-IT")}</td>
                    <td className="p-4 text-right tabular-nums font-medium">{p.leads}</td>
                    <td className="p-4 text-right tabular-nums">{p.conv_rate}%</td>
                    <td className="p-4 text-center hidden md:table-cell">
                      <Badge kind={p.badge} />
                    </td>
                    <td className="p-4 text-right">
                      {p.views >= 10 ? (
                        <button
                          onClick={() => onCompare(p.id)}
                          disabled={comparing === p.id}
                          className="text-xs text-[#7C8B6E] underline underline-offset-4 hover:opacity-80 disabled:opacity-40"
                          data-testid={`analytics-compare-${p.id}`}
                        >
                          {comparing === p.id ? "Analizzo..." : "Confronta annunci"}
                        </button>
                      ) : (
                        <span className="text-xs text-[#9A9892]">poco traffico</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI Insight modal-ish (inline card) */}
        {insight && (
          <div className="card-flat p-8 mt-8 border-l-4 border-[#7C8B6E]" data-testid="analytics-insight">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <span className="overline">Confronto annunci</span>
                <h3 className="font-editorial text-2xl mt-2 leading-tight">
                  {insight.headline}
                </h3>
                <p className="text-sm text-[#6B6A65] mt-2">
                  <span className="font-medium">{insight.target?.title}</span> ({insight.target?.conv_rate}%)
                  {" "}vs{" "}
                  <span className="font-medium">{insight.best?.title}</span> ({insight.best?.conv_rate}%)
                </p>
              </div>
              <button onClick={() => setInsight(null)} className="text-sm text-[#6B6A65] hover:text-[#7C8B6E]" data-testid="insight-close">Chiudi</button>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs uppercase tracking-wider text-[#6B6A65]">Fattori chiave</h4>
              {(insight.factors || []).map((f, i) => (
                <div key={i} className="border-l-2 border-[#E5E7E0] pl-4 py-1" data-testid={`insight-factor-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{f.label}</span>
                    <ImpactBadge impact={f.impact} />
                  </div>
                  <div className="text-sm text-[#6B6A65] mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    <span>Tuo annuncio: <strong className="text-[#1C1C1A]">{f.target_value}</strong></span>
                    <span>Riferimento: <strong className="text-[#1C1C1A]">{f.best_value}</strong></span>
                  </div>
                  <div className="text-sm text-[#1C1C1A] mt-1">{f.explanation}</div>
                </div>
              ))}
            </div>

            {Array.isArray(insight.actions) && insight.actions.length > 0 && (
              <div className="mt-6 border-t border-[#E5E7E0] pt-5">
                <h4 className="text-xs uppercase tracking-wider text-[#6B6A65] mb-3">Azioni concrete</h4>
                <ul className="space-y-2">
                  {insight.actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" data-testid={`insight-action-${i}`}>
                      <Sparkle size={14} weight="fill" className="text-[#7C8B6E] mt-1 flex-shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-editorial text-3xl text-[#7C8B6E] leading-none">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#6B6A65] mt-2">{label}</div>
    </div>
  );
}

function Badge({ kind }) {
  if (kind === "best") return <span className="inline-flex items-center gap-1 text-xs text-[#7C8B6E] bg-[#7C8B6E]/10 px-2 py-1 rounded-full"><ArrowUp size={11} weight="bold" /> Top</span>;
  if (kind === "low") return <span className="inline-flex items-center gap-1 text-xs text-[#7C8B6E] bg-[#7C8B6E]/10 px-2 py-1 rounded-full"><ArrowDown size={11} weight="bold" /> Sotto media</span>;
  if (kind === "ok") return <span className="inline-flex items-center gap-1 text-xs text-[#6B6A65]"><Minus size={11} weight="bold" /> Media</span>;
  return <span className="text-xs text-[#9A9892]">—</span>;
}

function ImpactBadge({ impact }) {
  const map = {
    alto: { color: "#7C8B6E", label: "alto" },
    medio: { color: "#7C8B6E", label: "medio" },
    basso: { color: "#6B6A65", label: "basso" },
  };
  const m = map[impact?.toLowerCase()] || { color: "#6B6A65", label: impact };
  return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${m.color}15`, color: m.color }}>{m.label}</span>;
}
